import { Flex } from "@otrium/core";
import { useCallback, useMemo } from "react";
import {
  useClearRefinements,
  useCurrentRefinements,
  useRange,
} from "react-instantsearch-core-v7";
import {
  ColorBox,
  StyledCloseButton,
  StyledText,
  Button,
} from "./Facets.styled";
import { Trans } from "@lingui/macro";
import {
  CATEGORY_PAGE_ID_FACET,
  COLOUR_FACET,
  PRICE_FACET,
} from "src/utils/algolia";
import { theme } from "src/theme";
import { Price } from "src/atoms/Price";
import {
  queryStringKeyMap,
  queryStringValuePosMap,
  removeAllFilters,
  removeAllFiltersByKey,
  removePriceFilters,
} from "src/utils/handle-search-params";
import { CurrentRefinementsConnectorParamsRefinement } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

type ActiveFiltersProps = {
  trackFilterReset: () => void;
};

function ActiveFilters({
  trackFilterReset,
}: ActiveFiltersProps): JSX.Element | null {
  const { refine: clearRefinements } = useClearRefinements({
    excludedAttributes: [CATEGORY_PAGE_ID_FACET],
  });
  const { items, refine } = useCurrentRefinements({
    excludedAttributes: [CATEGORY_PAGE_ID_FACET],
  });

  const { range } = useRange({ attribute: PRICE_FACET });

  const filters = useMemo(() => {
    return items.flatMap((item) => item.refinements);
  }, [items]);

  const priceFilters = useMemo(() => {
    const values = filters.filter((item) => item.attribute === PRICE_FACET);

    return {
      min:
        Number(values.find((item) => item.operator === ">=")?.value) ||
        range.min,
      max:
        Number(values.find((item) => item.operator === "<=")?.value) ||
        range.max,
    };
  }, [filters, range.min, range.max]);

  const handlePriceFilter = useCallback(() => {
    filters
      .filter((item) => item.attribute === PRICE_FACET)
      .forEach((item) => {
        refine(item);
      });

    removePriceFilters();
  }, [filters, refine]);

  const handleClearRefinements = useCallback(() => {
    removeAllFilters();

    clearRefinements();

    trackFilterReset();
  }, [clearRefinements, trackFilterReset]);

  const handleClearRefinement = useCallback(
    (item: CurrentRefinementsConnectorParamsRefinement) => {
      const splitVal = (item.value as string).split("||");
      removeAllFiltersByKey(
        queryStringKeyMap[item.attribute],
        splitVal[queryStringValuePosMap[item.attribute]]
      );

      refine(item);
    },
    [refine]
  );

  if (!filters.length) {
    return null;
  }

  return (
    <Flex gap={theme.space.space8} flexWrap="wrap">
      {filters
        .filter((item) => item.attribute !== PRICE_FACET)
        .map((item, idx) => {
          const isCategoryPageId = item.attribute === CATEGORY_PAGE_ID_FACET;
          const splitValue = item.label.split(isCategoryPageId ? " > " : "||");

          return (
            <Flex
              key={`filter-${item.attribute}-${splitValue[0]}-${idx}`}
              variant="centered"
            >
              {item.attribute === COLOUR_FACET ? (
                <ColorBox
                  id={splitValue[0]}
                  hex={splitValue[2]}
                  icon={splitValue[2] ? undefined : multicolorIconLink}
                />
              ) : null}

              <StyledText>
                {isCategoryPageId ? splitValue.at(-1) : splitValue[1]}
              </StyledText>
              <StyledCloseButton onClick={() => handleClearRefinement(item)} />
            </Flex>
          );
        })}

      {filters.filter((item) => item.attribute === PRICE_FACET).length ? (
        <Flex variant="centered">
          <StyledText>
            <Price price={Number(priceFilters.min)} /> -{" "}
            <Price price={Number(priceFilters.max)} />
          </StyledText>
          <StyledCloseButton onClick={handlePriceFilter} />
        </Flex>
      ) : null}

      <Button onClick={handleClearRefinements} data-testid="reset-filters">
        <Trans>Reset all filters</Trans>
      </Button>
    </Flex>
  );
}

export { ActiveFilters };
